import React, { createContext, useContext, useEffect, useState } from "react";
import { round2, useAppContext } from "./AppContext";

const OrderContext = createContext();
const CURRENT_HOTEL_ORDER = "currentHotelOrder";

export const OrderProvider = ({ children }) => {
  const {
    getCreditCardFeePercent,
    getAgentCommissionPercent,
    isGlobalConstantLoaded,
  } = useAppContext();

  const [saveInfoFunc, setSaveInfoFunc] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);

  const [currentHotelOrder, _setCurrentHotelOrder] = useState(null);

  const setHandleFeeData = (type, value) => {
    if (!currentHotelOrder) return;
    const ourPrice = currentHotelOrder.ourPrice;
    if (!ourPrice) return;
    ourPrice.handleFeeData = {
      type: type,
      value: value,
    };

    currentHotelOrder.ourPrice = ourPrice;
    currentHotelOrder.ourPrice = calcOurTotalPrice(currentHotelOrder);

    setCurrentHotelOrder(currentHotelOrder);
  };

  const setCurrentHotelOrder = (newCurrentHotelOrder) => {
    if (newCurrentHotelOrder) {
      newCurrentHotelOrder.ourPrice = calcOurTotalPrice(newCurrentHotelOrder);
    }
    _setCurrentHotelOrder({
      ...currentHotelOrder,
      ...newCurrentHotelOrder,
    });
  };

  // Helper function to create a lightweight version of the order for localStorage
  const createStorableOrder = (order) => {
    if (!order) return null;

    // Create a lightweight copy excluding large data arrays
    const lightOrder = { ...order };

    // Remove large data that can be refetched
    if (lightOrder.selectedRoom) {
      lightOrder.selectedRoom = {
        ...lightOrder.selectedRoom,
        images: [], // Remove images array
        facilities: lightOrder.selectedRoom.facilities
          ? lightOrder.selectedRoom.facilities.slice(0, 5)
          : [], // Keep only first 5 facilities
      };
    }

    if (lightOrder.selectedHotel) {
      lightOrder.selectedHotel = {
        ...lightOrder.selectedHotel,
        images: [], // Remove hotel images
        facilities: lightOrder.selectedHotel.facilities
          ? lightOrder.selectedHotel.facilities.slice(0, 5)
          : [], // Keep only first 5 facilities
      };
    }

    return lightOrder;
  };

  useEffect(() => {
    if (!currentHotelOrder) {
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
      return;
    }

    try {
      // Save only essential data to localStorage
      const storableOrder = createStorableOrder(currentHotelOrder);
      localStorage.setItem(CURRENT_HOTEL_ORDER, JSON.stringify(storableOrder));
    } catch (error) {
      console.warn("Failed to save order to localStorage:", error);
      // If still too large, save only the most essential data
      try {
        const minimalOrder = {
          searchPhrase: currentHotelOrder.searchPhrase,
          selectedHotelPackage: currentHotelOrder.selectedHotelPackage,
          checkInOutInfo: currentHotelOrder.checkInOutInfo,
          ourPrice: currentHotelOrder.ourPrice,
        };
        localStorage.setItem(CURRENT_HOTEL_ORDER, JSON.stringify(minimalOrder));
      } catch (minimalError) {
        console.error("Failed to save even minimal order data:", minimalError);
        localStorage.removeItem(CURRENT_HOTEL_ORDER);
      }
    }
  }, [currentHotelOrder]);

  useEffect(() => {
    if (currentHotelOrder && isGlobalConstantLoaded) {
      const updatedPrice = calcOurTotalPrice(currentHotelOrder);
      if (
        JSON.stringify(currentHotelOrder.ourPrice) !==
        JSON.stringify(updatedPrice)
      ) {
        const updatedOrder = { ...currentHotelOrder, ourPrice: updatedPrice };
        _setCurrentHotelOrder(updatedOrder);
      }
    }
  }, [isGlobalConstantLoaded]);

  const removeCurrentHotelOrder = () => {
    _setCurrentHotelOrder(null);
    localStorage.removeItem(CURRENT_HOTEL_ORDER);
  };

  // Function to clear localStorage if it gets too full
  const clearStorageIfNeeded = () => {
    try {
      // Test if we can write to localStorage
      const testKey = "storage_test";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
    } catch (error) {
      console.warn("localStorage is full, clearing hotel order data");
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
    }
  };

  const calculateHandleFee = (ourPrice) => {
    const priceWithCommission = ourPrice.priceWithCommission;

    const handleFeeData = ourPrice.handleFeeData;

    if (!handleFeeData || !handleFeeData.value || !handleFeeData.type) return 0;

    let handlingFee = handleFeeData.value;

    if (handleFeeData.type === "%")
      handlingFee = (priceWithCommission * handleFeeData.value) / 100;

    return handlingFee;
  };

  const calcOurTotalPrice = (currentHotelOrder) => {
    let ourPrice = currentHotelOrder.ourPrice;
    if (!ourPrice) {
      if (!currentHotelOrder.selectedHotelPackage) return null;
      ourPrice = currentHotelOrder.selectedHotelPackage.ourPrice;
    }

    ourPrice.handlingFee = calculateHandleFee(ourPrice);

    ourPrice.agentCommission =
      (getAgentCommissionPercent() / 100) * ourPrice.priceWithCommission;

    ourPrice.priceWithHandleFee =
      ourPrice.priceWithCommission + ourPrice.handlingFee;

    ourPrice.creditCardFee =
      (ourPrice.priceWithCommission +
        ourPrice.handlingFee +
        ourPrice.agentCommission) *
      (getCreditCardFeePercent() / 100);

    ourPrice.priceWithCreditCardFee = round2(
      ourPrice.priceWithHandleFee + ourPrice.creditCardFee
    );

    return ourPrice;
  };

  useEffect(() => {
    try {
      const data = localStorage.getItem(CURRENT_HOTEL_ORDER);
      const parsedData = data && data.length > 0 ? JSON.parse(data) : null;
      if (parsedData) {
        // Initialize with the stored data, but don't trigger a save immediately
        _setCurrentHotelOrder(parsedData);
      }
    } catch (error) {
      console.warn("Failed to load order from localStorage:", error);
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
    }
  }, []);

  return (
    <OrderContext.Provider
      value={{
        currentHotelOrder,
        setCurrentHotelOrder,
        removeCurrentHotelOrder,
        setHandleFeeData,
        saveInfoFunc,
        setSaveInfoFunc,
        setSavingInfo,
        savingInfo,
        clearStorageIfNeeded,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error("useOrder must be used within a OrderProvider");
  }
  return context;
};

export default OrderContext;
